import { useState, useMemo } from "react";
import { UNITS, convert, formatNumber } from "@/lib/units";
import ResultsPanel, { CalcPageHeader, Field } from "@/components/ResultsPanel";

const LEN_UNITS = [
  { key: "ft", label: "Feet", short: "ft", inMetres: 0.3048 },
  { key: "m", label: "Metres", short: "m", inMetres: 1 },
  { key: "yd", label: "Yards", short: "yd", inMetres: 0.9144 },
];

export default function PlotCalculator() {
  const [length, setLength] = useState("60");
  const [width, setWidth] = useState("40");
  const [lenUnit, setLenUnit] = useState("ft");

  const { areaSqm, perimM } = useMemo(() => {
    const L = Number(length) || 0;
    const W = Number(width) || 0;
    const factor = LEN_UNITS.find((u) => u.key === lenUnit).inMetres;
    return { areaSqm: L * W * factor * factor, perimM: 2 * (L + W) * factor };
  }, [length, width, lenUnit]);

  const rows = UNITS.map((u) => [`Area · ${u.label}`, `${formatNumber(convert(areaSqm, "sqm", u.key))} ${u.short}`]);
  rows.unshift(["Perimeter (m)", `${formatNumber(perimM)} m`]);
  rows.unshift(["Perimeter (ft)", `${formatNumber(perimM / 0.3048)} ft`]);

  return (
    <div>
      <CalcPageHeader title="Plot Size Calculator" description="Get area (in every land unit) and perimeter for any rectangular plot." slug="plot" />
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        <div className="lg:col-span-7">
          <div className="rounded-lg border border-border bg-card p-5 sm:p-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Length">
                <input data-testid="plot-length-input" type="number" value={length} onChange={(e)=>setLength(e.target.value)} className="w-full h-11 px-3 rounded-md border border-border bg-background font-mono outline-none focus:ring-2 focus:ring-primary/40" />
              </Field>
              <Field label="Width">
                <input data-testid="plot-width-input" type="number" value={width} onChange={(e)=>setWidth(e.target.value)} className="w-full h-11 px-3 rounded-md border border-border bg-background font-mono outline-none focus:ring-2 focus:ring-primary/40" />
              </Field>
            </div>
            <Field label="Length unit">
              <select data-testid="plot-length-unit" value={lenUnit} onChange={(e)=>setLenUnit(e.target.value)} className="w-full h-11 px-3 rounded-md border border-border bg-background text-sm outline-none focus:ring-2 focus:ring-primary/40">
                {LEN_UNITS.map((u)=><option key={u.key} value={u.key}>{u.label}</option>)}
              </select>
            </Field>
          </div>
        </div>
        <div className="lg:col-span-5 lg:sticky lg:top-24">
          <ResultsPanel
            title="Plot Size Calculator"
            slug="plot"
            highlight={{ label: "Area", value: `${formatNumber(areaSqm)} m²`, sub: `${formatNumber(convert(areaSqm,"sqm","sqft"))} sq ft · ${formatNumber(convert(areaSqm,"sqm","bigha"))} bigha` }}
            rows={rows}
            onReset={()=>{setLength("");setWidth("");}}
          />
        </div>
      </div>
    </div>
  );
}
